import { useIsFocused } from "@react-navigation/native";
import React from "react";
import {
  Animated,
  Platform,
  StatusBar,
  StyleSheet,
  View,
  AppState,
} from "react-native";
import { getData } from "@lib/storage";
import { isCLApp } from "../../config";
import { width } from "../../constants/metrics";
import { getCMSEntityById } from "../../services/cmsV2-api";
import CircularLoader from "./CircularLoader";
import StoryContainer from "./StoryContainer";

const perspective = width;
const angle = Math.atan(perspective / (width / 2));
const ratio = Platform.OS === "ios" ? 2 : 1.2;

class Stories extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      x: new Animated.Value(0),
      currentStory: 0,
      images: [],
      isLoading: true,
      isPaused: false,
      appInForeground: false,
      appState: AppState.currentState,
    };
    this.scroll = React.createRef();

    this.scrollForward = this.scrollForward.bind(this);
    this.scrollBackward = this.scrollBackward.bind(this);
  }

  async componentDidMount() {
    AppState.addEventListener("change", this._handleAppStateChange);
    const {
      route: {
        params: { cmsIds, userId, list_position },
      },
    } = this.props;

    const submittedPolls = await getData("cmsSubmittedPolls");
    try {
      const cmsData = await Promise.all(
        cmsIds.map((cms) =>
          getCMSEntityById({ complete: true, id: cms.id, user_id: userId }),
        ),
      );

      const images = cmsData.map((cms, index) => {
        const { data: { list_items = [], cl_title, cms_item_id } = {} } =
          cms || {};

        return Array.isArray(list_items)
          ? list_items.map((dirtyItem) => {
              const item = {
                id: cms_item_id,
                cms_item_id: dirtyItem.cms_item_id,
                image:
                  dirtyItem.sub_type === "video_card_with_details"
                    ? dirtyItem.video_link
                    : dirtyItem.image_link,
                sub_type: dirtyItem.sub_type,
                sharingTemplate: dirtyItem.sharingTemplates,
                link: dirtyItem.link,
                link_type: dirtyItem.link_type,
                cl_title,
                duration: dirtyItem.autoscroll_millisecond || 5000,
                story_name: isCLApp ? dirtyItem.cl_title : dirtyItem.cx_title,
                list_position:
                  index === list_position ? index : list_position + index,
                poll: dirtyItem.poll,
                poll_id: dirtyItem.poll_id,
                sharingLink: dirtyItem.sharingLink,
              };
              return item;
            })
          : [];
      });

      this.setState(
        (s) => ({
          ...s,
          images,
          currentStory: list_position,
          submittedPolls,
        }),
        () => {
          setTimeout(() => {
            this.scroll.current?.scrollTo({
              x: width * list_position,
              y: 0,
              animated: false,
            });
            this.setState((s) => ({ ...s, isLoading: false }));
          }, 0);
        },
      );
    } catch (error) {
      console.log(error);
      console.log(error?.response);
      this.setState({
        isLoading: false,
      });
    }
  }

  componentWillUnmount() {
    AppState.removeEventListener("change", this._handleAppStateChange);
  }

  getStyle(index) {
    const { x } = this.state;
    const offset = index * width;

    const inputRange = [offset - width, offset + width];

    const translateX = x.interpolate({
      inputRange,
      outputRange: [width / ratio, -width / ratio],
      extrapolate: "clamp",
    });
    const rotateY = x.interpolate({
      inputRange,
      outputRange: [`${angle}rad`, `-${angle}rad`],
      extrapolate: "clamp",
    });

    const translateX1 = x.interpolate({
      inputRange,
      outputRange: [width / 2, -width / 2],
      extrapolate: "clamp",
    });

    const extra = width / ratio / Math.cos(angle / 2) - width / ratio;
    const translateX2 = x.interpolate({
      inputRange,
      outputRange: [-extra, extra],
      extrapolate: "clamp",
    });

    return {
      ...StyleSheet.absoluteFill,
      transform: [
        { perspective },
        { translateX },
        { rotateY },
        { translateX: translateX1 },
        { translateX: translateX2 },
      ],
    };
  }

  getMaskStyle(index) {
    const { x } = this.state;
    const offset = index * width;
    const inputRange = [offset - width, offset, offset + width];
    const opacity = x.interpolate({
      inputRange,
      outputRange: [0.75, 0, 0.75],
      extrapolate: "clamp",
    });
    return {
      backgroundColor: "black",
      ...StyleSheet.absoluteFill,
      opacity,
    };
  }

  _handleAppStateChange = (nextAppState) => {
    if (
      this.state.appState.match(/inactive|background/) &&
      nextAppState === "active"
    ) {
      this.setState((s) => ({ ...s, appInForeground: !s.appInForeground }));
    }
    this.setState({ appState: nextAppState });
  };

  scrollForward() {
    if (this.state.currentStory < this.state.images.length - 1) {
      this.setState(
        (s) => ({
          currentStory: s.currentStory + 1,
        }),
        () => {
          this.scroll.current?.scrollTo({
            x: this.state.currentStory * width,
            y: 0,
            animated: true,
          });
        },
      );
    } else {
      this.props.navigation.goBack();
    }
  }

  scrollBackward() {
    if (this.state.currentStory > 0) {
      this.setState(
        (s) => ({
          currentStory: s.currentStory - 1,
        }),
        () => {
          this.scroll.current?.scrollTo({
            x: this.state.currentStory * width - 1,
            y: 0,
            animated: true,
          });
        },
      );
    }
  }

  render() {
    const { x, isLoading } = this.state;
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: "black",
        }}
      >
        {isLoading && (
          <View
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              zIndex: 100,
            }}
          >
            <CircularLoader />
          </View>
        )}
        {this.state.images.map((stories, i) => (
          <Animated.View style={this.getStyle(i)} key={i}>
            <StoryContainer
              currentList={i}
              isActive={i === this.state.currentStory}
              data={stories}
              scrollForward={this.scrollForward}
              scrollBackward={this.scrollBackward}
              isScrolling={this.state.isPaused}
              submittedPolls={this.state.submittedPolls}
              appInForeground={this.state.appInForeground}
            />
            <Animated.View style={this.getMaskStyle(i)} />
          </Animated.View>
        ))}
        <Animated.ScrollView
          ref={this.scroll}
          style={StyleSheet.absoluteFill}
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          snapToInterval={width}
          contentContainerStyle={{ width: width * this.state.images.length }}
          onScrollBeginDrag={() => this.setState({ isPaused: true })}
          disableIntervalMomentum={true}
          onMomentumScrollEnd={(event) => {
            this.setState({
              currentStory: Math.round(
                event.nativeEvent.contentOffset.x / width,
              ),
              isPaused: false,
            });
          }}
          onScroll={Animated.event(
            [
              {
                nativeEvent: {
                  contentOffset: { x },
                },
              },
            ],
            { useNativeDriver: true },
          )}
          decelerationRate="fast"
          horizontal
          pagingEnabled={false}
        />
      </View>
    );
  }
}

export default Stories;
